import {
    Box,
    ContextualBanner,
    Heading,
    LinkText,
    PasswordInput,
    RadioInput,
    TextBlock,
    TextInput,
} from "@bcc-ui/ui-components";
import { Form, Formik, FormikProps } from "formik";
import { t } from "i18next";
import React from "react";
import { object, string } from "yup";

import style from "./single-sign-on-settings-form.scss";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { SsoConfiguration, SsoProtocol } from "domain/users";

import testIds from "testIds.json";

export interface SsoFormValues {
    protocol: SsoProtocol;
    metadataUrl: string;
    clientId: string;
    clientSecret: string;
    oidcIssuer: string;
}

interface Props {
    ssoLoginDetails: Record<SsoProtocol, SsoConfiguration>;
    isPreviousConfigurationDeleted: boolean;
    setIsDirty: (isDirty: boolean) => void;
    setIsValid: (isValid: boolean) => void;
    setSubmitCount: (submitCount: number) => void;
    onSubmit: (values: SsoFormValues) => void;
}

interface FormStateSyncProps {
    isDirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
    isValid: boolean;
    setIsValid: (isValid: boolean) => void;
    submitCount: number;
    setSubmitCount: (count: number) => void;
}

export const CREATE_SSO_CONFIGURATION_FORM_ID = "createSsoConfigurationForm";

// Sync form state with parent component via callbacks
const FormStateSync = (props: FormStateSyncProps) => {
    React.useEffect(() => props.setIsDirty(props.isDirty), [props.isDirty, props.setIsDirty]);
    React.useEffect(() => props.setIsValid(props.isValid), [props.isValid, props.setIsValid]);
    React.useEffect(() => props.setSubmitCount(props.submitCount), [props.submitCount, props.setSubmitCount]);
    return null;
};

export const SingleSignOnSettingsForm = (props: Props) => {
    const renderBanner = () => {
        if (props.isPreviousConfigurationDeleted) {
            return (
                <ContextualBanner variant="Warning">
                    <Box columnLayout>
                        <TextBlock disableBottomSpacing>
                            {t("Settings.ssoConfigurations.previousConfigurationDeleted")}
                        </TextBlock>
                        <TextBlock disableBottomSpacing>
                            {t("Settings.ssoConfigurations.message")}{" "}
                            <LinkText as="a" href={"/knowledge-base"}>
                                {t("Settings.ssoConfigurations.weblink")}
                            </LinkText>
                            {"."}
                        </TextBlock>
                    </Box>
                </ContextualBanner>
            );
        }
        return (
            <ContextualBanner variant="Info">
                <TextBlock disableBottomSpacing>
                    {t("Settings.ssoConfigurations.message")}{" "}
                    <LinkText as="a" href={"/knowledge-base"}>
                        {t("Settings.ssoConfigurations.weblink")}
                    </LinkText>
                </TextBlock>
            </ContextualBanner>
        );
    };

    return (
        <Formik<SsoFormValues>
            initialValues={{
                protocol: SsoProtocol.SAML,
                metadataUrl: "",
                clientId: "",
                clientSecret: "",
                oidcIssuer: "",
            }}
            validationSchema={object().shape({
                metadataUrl: string().when("protocol", {
                    is: SsoProtocol.SAML,
                    then: string().required(t("Settings.ssoMetadataUrlValidation")).min(8).max(250).url(),
                }),

                clientId: string().when("protocol", {
                    is: SsoProtocol.OIDC,
                    then: string().required(t("Settings.ssoClientIdValidation")).min(1).max(250),
                }),
                clientSecret: string().when("protocol", {
                    is: SsoProtocol.OIDC,
                    then: string().required(t("Settings.ssoClientSecretValidation")).min(1).max(250),
                }),
                oidcIssuer: string().when("protocol", {
                    is: SsoProtocol.OIDC,
                    then: string().required(t("Settings.ssoOidcIssuerValidation")).min(1).max(250).url(),
                }),
            })}
            onSubmit={props.onSubmit}
        >
            {({
                values,
                initialValues,
                handleChange,
                dirty,
                isValid,
                errors,
                submitCount,
                resetForm,
            }: FormikProps<SsoFormValues>) => {
                const ssoLoginDetails = props.ssoLoginDetails[values.protocol];

                return (
                    <Form id={CREATE_SSO_CONFIGURATION_FORM_ID} className={style.formContent}>
                        <FormStateSync
                            isDirty={dirty}
                            setIsDirty={props.setIsDirty}
                            isValid={isValid}
                            setIsValid={props.setIsValid}
                            submitCount={submitCount}
                            setSubmitCount={props.setSubmitCount}
                        />

                        {renderBanner()}

                        <Box columnLayout gap="Lg" paddingTop="Md">
                            <Box columnLayout>
                                <Heading variant="h2" underline>
                                    {t("Settings.ssoConfigurations.idpSettings")}
                                </Heading>

                                <Box columnLayout gap="Xxs">
                                    <Heading variant="SUBTITLE_2">
                                        {t("Settings.ssoConfigurations.chooseProtocol")}:
                                    </Heading>

                                    <RadioInput
                                        id="samlProtocol"
                                        name="protocol"
                                        label={t("Settings.ssoConfigurations.saml")}
                                        value={SsoProtocol.SAML}
                                        checked={values.protocol === SsoProtocol.SAML}
                                        onChange={() =>
                                            resetForm({ values: { ...initialValues, protocol: SsoProtocol.SAML } })
                                        }
                                        testId={testIds.workArea.settings.sso.protocolRadioButton.saml}
                                    />

                                    <RadioInput
                                        id="oidcProtocol"
                                        name="protocol"
                                        label={t("Settings.ssoConfigurations.oidc")}
                                        value={SsoProtocol.OIDC}
                                        checked={values.protocol === SsoProtocol.OIDC}
                                        onChange={() =>
                                            resetForm({ values: { ...initialValues, protocol: SsoProtocol.OIDC } })
                                        }
                                        testId={testIds.workArea.settings.sso.protocolRadioButton.oidc}
                                    />
                                </Box>

                                <Box columnLayout paddingTop="Md" gap="Xxxs">
                                    <Box columnLayout>
                                        <Heading variant="SUBTITLE_2">
                                            {t("Settings.ssoConfigurations.singleSignOnURL")}
                                        </Heading>

                                        <Box verticalAlignment="Center">
                                            {ssoLoginDetails.ssoUrl}
                                            <CopyToClipboard
                                                value={ssoLoginDetails.ssoUrl != null ? ssoLoginDetails.ssoUrl : " "}
                                                isNewTable
                                            />
                                        </Box>
                                    </Box>

                                    {values.protocol === SsoProtocol.SAML && (
                                        <Box columnLayout>
                                            <Heading variant="SUBTITLE_2">
                                                {t("Settings.ssoConfigurations.serviceProviderID")}
                                            </Heading>

                                            <Box verticalAlignment="Center">
                                                {ssoLoginDetails.entityId}
                                                <CopyToClipboard
                                                    value={
                                                        ssoLoginDetails.entityId != null
                                                            ? ssoLoginDetails.entityId
                                                            : " "
                                                    }
                                                    isNewTable
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            <Box columnLayout>
                                <Heading variant="h2" underline>
                                    {t("Settings.ssoConfigurations.configuration")}
                                </Heading>

                                <TextBlock>{t("Settings.ssoConfigurations.note")}</TextBlock>

                                <Box columnLayout gap="Xs">
                                    {values.protocol === SsoProtocol.SAML && (
                                        <TextInput
                                            name="metadataUrl"
                                            id="metadataUrl"
                                            label={t("Settings.ssoConfigurations.samlMetadataURL")}
                                            value={values.metadataUrl}
                                            onChange={handleChange}
                                            error={submitCount > 0 ? errors.metadataUrl : undefined}
                                            testIds={{
                                                input: testIds.workArea.settings.sso.metadataUrlInput.itself,
                                                error: testIds.workArea.settings.sso.metadataUrlInput.errorLabel,
                                            }}
                                        />
                                    )}

                                    {values.protocol === SsoProtocol.OIDC && (
                                        <>
                                            <TextInput
                                                name="clientId"
                                                id="clientId"
                                                label={t("Settings.ssoConfigurations.clientID")}
                                                value={values.clientId}
                                                onChange={handleChange}
                                                error={submitCount > 0 ? errors.clientId : undefined}
                                                testIds={{
                                                    input: testIds.workArea.settings.sso.clientIdInput.itself,
                                                    error: testIds.workArea.settings.sso.clientIdInput.errorLabel,
                                                }}
                                            />

                                            <PasswordInput
                                                name="clientSecret"
                                                id="clientSecret"
                                                label={t("Settings.ssoConfigurations.clientSecret")}
                                                value={values.clientSecret}
                                                onChange={handleChange}
                                                autoComplete="new-password"
                                                error={submitCount > 0 ? errors.clientSecret : undefined}
                                                testIds={{
                                                    input: testIds.workArea.settings.sso.clientSecretInput.itself,
                                                    error: testIds.workArea.settings.sso.clientSecretInput.errorLabel,
                                                }}
                                            />

                                            <TextInput
                                                name="oidcIssuer"
                                                id="oidcIssuer"
                                                label={t("Settings.ssoConfigurations.issuerURL")}
                                                value={values.oidcIssuer}
                                                onChange={handleChange}
                                                error={submitCount > 0 ? errors.oidcIssuer : undefined}
                                                testIds={{
                                                    input: testIds.workArea.settings.sso.issuerUrlInput.itself,
                                                    error: testIds.workArea.settings.sso.issuerUrlInput.errorLabel,
                                                }}
                                            />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};
